'use client';

import React, { useState } from 'react';
import { Store, Key, Delete } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface RetailerPinGateProps {
  onAuthenticated: (staff: { name: string; role: string }) => void;
}

const ROLE_LABELS: Record<string, string> = {
  Owner: 'صاحب المحل / Propriétaire',
  Cashier: 'الكاشير / Caissier',
  Assistant: 'المساعد / Assistant',
};

export default function RetailerPinGate({ onAuthenticated }: RetailerPinGateProps) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDigit = (d: string) => {
    if (pin.length < 4) setPin(prev => prev + d);
  };

  const handleDelete = () => setPin(prev => prev.slice(0, -1));

  const handleSubmit = async () => {
    if (pin.length < 4) return;
    setLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { data, error: dbError } = await supabase
        .from('retailer_staff')
        .select('*')
        .eq('pin_code', pin)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (dbError || !data) {
        setError('كود PIN غير صحيح — Code PIN incorrect');
        setPin('');
      } else {
        onAuthenticated({
          name: data.staff_name,
          role: ROLE_LABELS[data.role_type] || data.role_type,
        });
      }
    } catch {
      setError('خطأ في الاتصال — Erreur de connexion');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const digits = ['1','2','3','4','5','6','7','8','9','','0','⌫'];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto mb-4">
            <Store size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-1">حانوت / Épicerie</h1>
          <p className="text-sm text-muted-foreground">أدخل كود PIN للدخول — Entrez votre PIN</p>
        </div>

        {/* PIN dots */}
        <div className="flex justify-center gap-4 mb-6">
          {[0,1,2,3].map(i => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-all ${
                i < pin.length
                  ? 'bg-emerald-600 border-emerald-600' :'border-border bg-transparent'
              }`}
            />
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 px-4 py-2 bg-danger/10 border border-danger/20 rounded-lg text-center">
            <p className="text-xs text-danger">{error}</p>
          </div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {digits.map((d, i) => {
            if (d === '') return <div key={i} />;
            if (d === '⌫') {
              return (
                <button
                  key={i}
                  onClick={handleDelete}
                  className="h-14 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                >
                  <Delete size={18} className="text-muted-foreground" />
                </button>
              );
            }
            return (
              <button
                key={i}
                onClick={() => handleDigit(d)}
                className="h-14 rounded-xl bg-card border border-border hover:bg-muted text-lg font-semibold text-foreground transition-colors"
              >
                {d}
              </button>
            );
          })}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={pin.length < 4 || loading}
          className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
        >
          <Key size={16} />
          {loading ? 'جاري التحقق...' : 'دخول — Entrer'}
        </button>

        {/* Demo hint */}
        <div className="mt-6 p-3 bg-muted rounded-xl">
          <p className="text-2xs text-muted-foreground text-center font-medium mb-2">أكواد تجريبية — Codes démo</p>
          <div className="space-y-1">
            <div className="flex justify-between text-2xs">
              <span className="text-muted-foreground">صاحب المحل / Propriétaire</span>
              <span className="font-mono font-bold text-foreground">1111</span>
            </div>
            <div className="flex justify-between text-2xs">
              <span className="text-muted-foreground">الكاشير / Caissier</span>
              <span className="font-mono font-bold text-foreground">2222</span>
            </div>
            <div className="flex justify-between text-2xs">
              <span className="text-muted-foreground">المساعد / Assistant</span>
              <span className="font-mono font-bold text-foreground">3333</span>
            </div>
          </div>
        </div>

        {/* Back to supplier */}
        <div className="mt-4 text-center">
          <a href="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            ← العودة إلى واجهة المورد — Retour Fournisseur
          </a>
        </div>
      </div>
    </div>
  );
}
